import { useState } from "react";
import { num } from "../../../lib/format";
import { Button } from "../../ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { Section } from "./section";

export interface AreaRow {
  id: string;
  description: string;
  eyrBuilt: number | null;
  area: number | null;
  multiple: number | null;
  rate: number | null;
  addGst: boolean;
  totalReinstatementExclFees: number | null;
  feesPercentage: number | null;
  totalReinstatementInclFees: number | null;
  firstYearMonths: number | null;
  demoPlanMonths: number | null;
  constructionMonths: number | null;
  reinstatementMonths: number | null;
  inflationPctYear1: number | null;
  inflationAmtYear1: number | null;
  inflationPctYear2: number | null;
  inflationAmtYear2: number | null;
  inflationPctYear3: number | null;
  inflationAmtYear3: number | null;
  inflationPctYear4: number | null;
  inflationAmtYear4: number | null;
  reinstatementInflationTotal: number | null;
  demolitionPercentage: number | null;
  demolitionRate: number | null;
  demolitionCost: number | null;
  percentagePa: number | null;
  depreciationTotalPercentage: number | null;
  depreciation: number | null;
  drcForFsl: number | null;
}

function sum(areas: AreaRow[], key: keyof AreaRow): number {
  return areas.reduce((s, r) => s + (Number(r[key]) || 0), 0);
}

function hasAny(areas: AreaRow[], key: keyof AreaRow): boolean {
  return areas.some((r) => r[key] != null && r[key] !== 0);
}

export function SectionAreas({ areas }: { areas: AreaRow[] }): React.ReactElement | null {
  const [fullView, setFullView] = useState(false);

  if (areas.length === 0) return null;

  // Adaptive columns — only show if at least one row has data
  const showRate = hasAny(areas, "rate");
  const showMultiple = hasAny(areas, "multiple");
  const showReinst = hasAny(areas, "totalReinstatementInclFees");
  const showDepreciation = hasAny(areas, "depreciation");
  const showDrc = hasAny(areas, "drcForFsl");
  const showInflTotal = hasAny(areas, "reinstatementInflationTotal");
  const showDemoCost = hasAny(areas, "demolitionCost");

  return (
    <Section id="areas" title="Areas">
      <div className="flex items-center gap-2 mb-3">
        <Button variant={fullView ? "outline" : "default"} size="sm" onClick={() => setFullView(false)}>
          Summary
        </Button>
        <Button variant={fullView ? "default" : "outline"} size="sm" onClick={() => setFullView(true)}>
          Full Detail
        </Button>
        <span className="text-xs text-muted-foreground ml-2">{areas.length} line items</span>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[200px]">Description</TableHead>
              {fullView && <TableHead className="text-right">E Yr</TableHead>}
              <TableHead className="text-right">Area</TableHead>
              {showMultiple && <TableHead className="text-right">Multiple</TableHead>}
              {showRate && <TableHead className="text-right">Rate</TableHead>}
              {fullView && <TableHead>GST</TableHead>}
              {showReinst && <TableHead className="text-right">Reinst. Incl Fees</TableHead>}
              {fullView && showInflTotal && <TableHead className="text-right">Infl Total</TableHead>}
              {fullView && showDemoCost && <TableHead className="text-right">Demo Cost</TableHead>}
              {showDepreciation && <TableHead className="text-right">Depreciation</TableHead>}
              {showDrc && <TableHead className="text-right">DRC for FENZ Levy</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {areas.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="font-medium whitespace-normal max-w-[300px]">{row.description}</TableCell>
                {fullView && <TableCell className="text-right tabular-nums">{row.eyrBuilt ?? "—"}</TableCell>}
                <TableCell className="text-right tabular-nums">{num(row.area)}</TableCell>
                {showMultiple && (
                  <TableCell className="text-right tabular-nums">{row.multiple?.toFixed(2) ?? "—"}</TableCell>
                )}
                {showRate && <TableCell className="text-right tabular-nums">{num(row.rate)}</TableCell>}
                {fullView && <TableCell>{row.addGst ? "Yes" : "No"}</TableCell>}
                {showReinst && (
                  <TableCell className="text-right tabular-nums font-medium">
                    {num(row.totalReinstatementInclFees)}
                  </TableCell>
                )}
                {fullView && showInflTotal && (
                  <TableCell className="text-right tabular-nums">{num(row.reinstatementInflationTotal)}</TableCell>
                )}
                {fullView && showDemoCost && (
                  <TableCell className="text-right tabular-nums">{num(row.demolitionCost)}</TableCell>
                )}
                {showDepreciation && <TableCell className="text-right tabular-nums">{num(row.depreciation)}</TableCell>}
                {showDrc && <TableCell className="text-right tabular-nums font-medium">{num(row.drcForFsl)}</TableCell>}
              </TableRow>
            ))}
            {/* Totals row */}
            <TableRow className="border-t-2 border-foreground/20 bg-muted/50 font-semibold">
              <TableCell>Totals</TableCell>
              {fullView && <TableCell />}
              <TableCell className="text-right tabular-nums">{num(sum(areas, "area"))}</TableCell>
              {showMultiple && <TableCell />}
              {showRate && <TableCell />}
              {fullView && <TableCell />}
              {showReinst && (
                <TableCell className="text-right tabular-nums">
                  {num(sum(areas, "totalReinstatementInclFees"))}
                </TableCell>
              )}
              {fullView && showInflTotal && (
                <TableCell className="text-right tabular-nums">
                  {num(sum(areas, "reinstatementInflationTotal"))}
                </TableCell>
              )}
              {fullView && showDemoCost && (
                <TableCell className="text-right tabular-nums">{num(sum(areas, "demolitionCost"))}</TableCell>
              )}
              {showDepreciation && (
                <TableCell className="text-right tabular-nums">{num(sum(areas, "depreciation"))}</TableCell>
              )}
              {showDrc && <TableCell className="text-right tabular-nums">{num(sum(areas, "drcForFsl"))}</TableCell>}
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </Section>
  );
}
